import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminOperations.css";
import Cabecera from "../Others/Cabecera";
import PiePagina from "../Others/PiePagina";
import BarraSuperior from "../Others/BarraSuperior";
import BarraInferior from "../Others/BarraInferior";
import { useData } from "./DataContext";
import { routeFindUserByRol, updateClase } from "./constants";

const normalizarFechaInput = (valor) => {
  if (!valor) return "";

  const texto = String(valor).trim();
  const fechaIso = texto.match(/^(\d{4}-\d{2}-\d{2})/);
  if (fechaIso) return fechaIso[1];

  const fecha = new Date(texto);
  if (Number.isNaN(fecha.getTime())) return "";

  const year = fecha.getUTCFullYear();
  const month = String(fecha.getUTCMonth() + 1).padStart(2, "0");
  const day = String(fecha.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const crearFormularioDesdeClase = (clase) => ({
  nombre_clase: clase?.nombre_clase || "",
  descripcion: clase?.descripcion || "",
  matricula_clase: clase?.matricula_clase || "",
  matricula_maestro: clase?.matricula_maestro || "",
  fecha_inicio: normalizarFechaInput(clase?.fecha_inicio),
  fecha_fin: normalizarFechaInput(clase?.fecha_fin),
});

export const AdminEditCourse = () => {
  const { dataClase, setClaseData } = useData();
  const navigate = useNavigate();
  const [formulario, setFormulario] = useState(() => crearFormularioDesdeClase(dataClase));
  const [maestros, setMaestros] = useState([]);
  const [cargandoMaestros, setCargandoMaestros] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    setFormulario(crearFormularioDesdeClase(dataClase));
  }, [dataClase]);

  useEffect(() => {
    const cargarMaestros = async () => {
      try {
        const response = await axios.get(routeFindUserByRol, {
          params: { role: "Maestro" },
        });
        const lista = response.data?.data ?? response.data ?? [];
        setMaestros(Array.isArray(lista) ? lista : []);
      } catch (error) {
        console.error("Error al cargar maestros:", error);
        setMaestros([]);
        setResultado({
          tipo: "error",
          mensaje: "No fue posible cargar la lista de maestros.",
        });
      } finally {
        setCargandoMaestros(false);
      }
    };

    cargarMaestros();
  }, []);

  const formularioOriginal = useMemo(
    () => crearFormularioDesdeClase(dataClase),
    [dataClase],
  );

  const hayCambios = JSON.stringify(formulario) !== JSON.stringify(formularioOriginal);

  const actualizarCampo = (campo, valor) => {
    setFormulario((actual) => ({ ...actual, [campo]: valor }));
    setResultado(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!dataClase?.id_clase) {
      setResultado({
        tipo: "error",
        mensaje: "No hay un curso seleccionado para editar.",
      });
      return;
    }

    if (!formulario.nombre_clase.trim() || !formulario.descripcion.trim()) {
      setResultado({
        tipo: "warning",
        mensaje: "El nombre y la descripción del curso son obligatorios.",
      });
      return;
    }

    if (!formulario.matricula_maestro) {
      setResultado({
        tipo: "warning",
        mensaje: "Selecciona al maestro responsable.",
      });
      return;
    }

    if (!formulario.fecha_inicio || !formulario.fecha_fin) {
      setResultado({
        tipo: "warning",
        mensaje: "La fecha de inicio y la fecha de fin son obligatorias.",
      });
      return;
    }

    if (formulario.fecha_fin < formulario.fecha_inicio) {
      setResultado({
        tipo: "warning",
        mensaje: "La fecha de fin no puede ser anterior a la fecha de inicio.",
      });
      return;
    }

    if (!hayCambios) {
      setResultado({
        tipo: "warning",
        mensaje: "No hay cambios por guardar.",
      });
      return;
    }

    const maestroSeleccionado = maestros.find(
      (maestro) => String(maestro.matricula) === String(formulario.matricula_maestro),
    );

    if (!maestroSeleccionado?.id) {
      setResultado({
        tipo: "error",
        mensaje: "No fue posible identificar al maestro seleccionado.",
      });
      return;
    }

    setGuardando(true);
    setResultado(null);

    try {
      const payload = {
        id_clase: dataClase.id_clase,
        nombre_clase: formulario.nombre_clase.trim(),
        descripcion: formulario.descripcion.trim(),
        matricula_maestro: formulario.matricula_maestro,
        matricula_clase: dataClase.matricula_clase,
        id_maestro: maestroSeleccionado.id,
        fecha_inicio: formulario.fecha_inicio,
        fecha_fin: formulario.fecha_fin,
      };

      const response = await axios.put(updateClase, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || "El servidor no confirmó la actualización.");
      }

      setClaseData({
        ...dataClase,
        ...payload,
      });
      setResultado({
        tipo: "success",
        mensaje: "El curso se actualizó correctamente.",
      });
    } catch (error) {
      console.error("Error al actualizar curso:", error);
      setResultado({
        tipo: "error",
        mensaje:
          error.response?.data?.message ||
          error.message ||
          "No fue posible actualizar el curso.",
      });
    } finally {
      setGuardando(false);
    }
  };

  const cursoDisponible = Boolean(dataClase?.id_clase && dataClase?.matricula_clase);

  return (
    <div className="contenedor-pagina">
      <Cabecera
        contenidosuperior={<BarraSuperior texto_cabecera={dataClase?.nombre_clase || "Editar curso"} />}
        contenidoInferior={<BarraInferior />}
      />

      <main className="admin-operation-page">
        <div className="admin-operation-container admin-operation-container-narrow">
          <header className="admin-operation-header">
            <p className="admin-operation-eyebrow">Administración académica</p>
            <h1>Editar curso</h1>
            <p>
              Actualiza los datos académicos de la materia. La matrícula permanece bloqueada para proteger las relaciones existentes del curso.
            </p>
          </header>

          {!cursoDisponible ? (
            <section className="admin-operation-panel">
              <div className="admin-operation-status admin-operation-status-warning" role="status">
                No hay un curso seleccionado. Regresa al inicio y abre primero la materia que deseas editar.
              </div>
              <div className="admin-operation-actions">
                <button className="admin-operation-button" type="button" onClick={() => navigate("/admin-home")}>
                  Regresar al inicio
                </button>
              </div>
            </section>
          ) : (
            <form className="admin-operation-panel" onSubmit={handleSubmit}>
              <div className="admin-operation-form-grid">
                <div className="admin-operation-field">
                  <label htmlFor="edit-course-name">Nombre de la clase</label>
                  <input
                    id="edit-course-name"
                    type="text"
                    value={formulario.nombre_clase}
                    onChange={(event) => actualizarCampo("nombre_clase", event.target.value)}
                    disabled={guardando}
                    required
                  />
                </div>

                <div className="admin-operation-field">
                  <label htmlFor="edit-course-id">Matrícula de la clase</label>
                  <input
                    id="edit-course-id"
                    type="text"
                    value={formulario.matricula_clase}
                    readOnly
                    aria-describedby="edit-course-id-help"
                  />
                  <small id="edit-course-id-help">Este identificador no se modifica desde la edición del curso.</small>
                </div>

                <div className="admin-operation-field admin-operation-field-wide">
                  <label htmlFor="edit-course-description">Descripción</label>
                  <textarea
                    id="edit-course-description"
                    value={formulario.descripcion}
                    onChange={(event) => actualizarCampo("descripcion", event.target.value)}
                    disabled={guardando}
                    required
                  />
                </div>

                <div className="admin-operation-field admin-operation-field-wide">
                  <label htmlFor="edit-course-teacher">Maestro responsable</label>
                  <select
                    id="edit-course-teacher"
                    value={formulario.matricula_maestro}
                    onChange={(event) => actualizarCampo("matricula_maestro", event.target.value)}
                    disabled={cargandoMaestros || guardando}
                    required
                  >
                    <option value="">
                      {cargandoMaestros ? "Cargando maestros..." : "Selecciona un maestro"}
                    </option>
                    {maestros.map((maestro) => (
                      <option key={maestro.id ?? maestro.matricula} value={maestro.matricula}>
                        {maestro.matricula} - {maestro.nombre} {maestro.apellido}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="admin-operation-field">
                  <label htmlFor="edit-course-start">Fecha de inicio</label>
                  <input
                    id="edit-course-start"
                    type="date"
                    value={formulario.fecha_inicio}
                    onChange={(event) => actualizarCampo("fecha_inicio", event.target.value)}
                    disabled={guardando}
                    required
                  />
                </div>

                <div className="admin-operation-field">
                  <label htmlFor="edit-course-end">Fecha de fin</label>
                  <input
                    id="edit-course-end"
                    type="date"
                    value={formulario.fecha_fin}
                    min={formulario.fecha_inicio || undefined}
                    onChange={(event) => actualizarCampo("fecha_fin", event.target.value)}
                    disabled={guardando}
                    required
                  />
                </div>
              </div>

              {resultado && (
                <div
                  className={`admin-operation-status admin-operation-status-${resultado.tipo}`}
                  role="status"
                  aria-live="polite"
                >
                  {resultado.mensaje}
                </div>
              )}

              <div className="admin-operation-actions">
                <button
                  className="admin-operation-button"
                  type="submit"
                  disabled={cargandoMaestros || maestros.length === 0 || guardando || !hayCambios}
                >
                  {guardando ? "Guardando cambios..." : "Guardar cambios"}
                </button>
                <button
                  className="admin-operation-button admin-operation-button-secondary"
                  type="button"
                  onClick={() => navigate("/a-pagina-tablon")}
                  disabled={guardando}
                >
                  Volver al curso
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <PiePagina imagenSrc={require("../Assets/lawbackground23.jpg")} />
      <footer>Grupo Derecho & Progreso &copy; 2024</footer>
    </div>
  );
};
